import React, { ReactNode, useRef, useState } from 'react';
import { colors, Stack, Paper, Popper, Typography } from '@mui/material';
import moment from 'moment';
import Box from '@mui/material/Box';
import Progress from '../Progress/Progress';
import { currencyFormat } from '../../utils/string';
import ExternalLink from '../ExternalLink/ExternalLink';
import Chip from '../Chip/Chip';
import { timeZone } from '../../utils/constants';
import { customDate } from '../../utils/date';

interface IProps {
  label: string;
  title?: ReactNode | number | string;
  type?: 'text' | 'progress' | 'Amount' | 'Date' | 'Status' | 'Link' | 'withStatus' | 'customDate' | 'Success';
  progress?: number | string;
  labelSx?: any;
  titleSx?: any;
  link?: string;
  status?: string;
  format?: string;
}

function isOverflown(element: Element): boolean {
  // return element.scrollHeight > element.clientHeight
  return element.scrollWidth > element.clientWidth;
}

const Label: React.FC<IProps> = ({
  label,
  title,
  status,
  type = 'text',
  progress = 0,
  labelSx,
  titleSx,
  link,
  format,
}) => {
  // refs
  const wrapper = useRef<any>(null);
  const labelValue = useRef<any>(null);
  const titleValue = useRef<any>(null);

  // local states
  const [showTitlePopper, setShowTitlePopper] = useState(true);
  const [showLabelPopper, setShowLabelPopper] = useState(true);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMouseEnter = (enterType: string) => {
    if (enterType === 'label') {
      const isCurrentlyOverflown = isOverflown(labelValue.current);
      setShowTitlePopper(false);
      setAnchorEl(labelValue.current);
      setShowLabelPopper(isCurrentlyOverflown);
    }
    if (enterType === 'title') {
      const isCurrentlyOverflown = isOverflown(titleValue.current);
      setShowLabelPopper(false);
      setShowTitlePopper(isCurrentlyOverflown);
      setAnchorEl(titleValue.current);
    }
  };

  const handleMouseLeave = () => {
    setShowTitlePopper(false);
    setShowLabelPopper(false);
  };

  return (
    <Box ref={wrapper}>
      <Typography
        onMouseEnter={() => handleMouseEnter('label')}
        onMouseLeave={handleMouseLeave}
        ref={labelValue}
        sx={labelSx}
        variant="caption"
        color={(theme) => `#717171`}
        fontSize={14}
        lineHeight={1.4}
        fontWeight={400}
        display="block"
        noWrap
      >
        {label}
      </Typography>
      {type?.toLowerCase() === 'text' &&
        (typeof title === 'string' || !title ? (
          <Typography
            onMouseEnter={() => handleMouseEnter('title')}
            onMouseLeave={handleMouseLeave}
            ref={titleValue}
            sx={titleSx}
            variant="body2"
            fontWeight={500}
            lineHeight={1.4}
            minWidth={100}
            fontSize={16}
            color="#3E3E3E"
            noWrap
          >
            {title || '--'}
          </Typography>
        ) : (
          title
        ))}

      {type?.toLowerCase() === 'withstatus' &&
        (typeof title === 'string' || !title ? (
          <Stack direction="row" gap={1} alignItems="center">
            <Typography
              onMouseEnter={() => handleMouseEnter('title')}
              onMouseLeave={handleMouseLeave}
              ref={titleValue}
              sx={titleSx}
              variant="body2"
              fontWeight={500}
              lineHeight={1.4}
              minWidth={100}
              fontSize={16}
              color="#3E3E3E"
              noWrap
            >
              {title || '--'}
            </Typography>
            <Chip text={status ?? '--'} />
          </Stack>
        ) : null)}

      {type?.toLowerCase() === 'status' &&
        (typeof title === 'string' || !title ? (
          <Typography
            color="#E04732"
            fontSize={12}
            fontWeight={500}
            sx={{
              minWidth: '104px',
              padding: '2px 0',
              lineHeight: 1,
              border: '1px solid #E04732',
              borderRadius: '2px',
              display: 'inline-block',
              textAlign: 'center',
              paddingLeft: '5px',
              paddingRight: '5px',
            }}
          >
            {title || '--'}
          </Typography>
        ) : (
          title
        ))}

      {type?.toLowerCase() === 'success' &&
        (typeof title === 'string' || !title ? (
          <Typography
            color="#288E49"
            fontSize={12}
            fontWeight={500}
            sx={{
              minWidth: '104px',
              padding: '2px 0',
              lineHeight: 1,
              border: '1px solid #288E49',
              borderRadius: '2px',
              display: 'inline-block',
              textAlign: 'center',
              paddingLeft: '5px',
              paddingRight: '5px',
            }}
          >
            {title || '--'}
          </Typography>
        ) : (
          title
        ))}
      {type?.toLowerCase() === 'amount' ? (
        <Typography
          ref={titleValue}
          sx={titleSx}
          variant="body2"
          fontWeight={500}
          minWidth={100}
          fontSize={14}
          color="#000"
          noWrap
        >
          {currencyFormat(title, 'USD', 'US') ?? '--'}
        </Typography>
      ) : null}
      {type?.toLowerCase() === 'date' ? (
        <Typography
          ref={titleValue}
          sx={titleSx}
          variant="body2"
          fontWeight={500}
          minWidth={100}
          fontSize={14}
          color="#000"
          noWrap
        >
          {typeof title === 'string' && title !== '--' && title !== '' && title !== 'N/A'
            ? moment(title, 'Y-MM-DD hh:mm:ss').format(format ?? 'MMM DD, Y')
            : '--'}
        </Typography>
      ) : null}
      {type?.toLowerCase() === 'customdate' ? (
        <Typography
          ref={titleValue}
          sx={titleSx}
          variant="body2"
          fontWeight={500}
          minWidth={100}
          fontSize={14}
          color="#000"
          noWrap
        >
          {typeof title === 'string' && title !== '--' && title !== '' && title !== 'N/A' ? customDate(title) : '--'}
        </Typography>
      ) : null}
      {type?.toLowerCase() === 'link' &&
        (typeof title === 'string' || !title ? (
          <ExternalLink
            ref={titleValue}
            style={{ ...titleSx, color: '#456BD9' }}
            onClick={link === '#' ? (e) => e.preventDefault() : () => null}
            href={link}
            target="_blank"
            rel="noreferrer"
          >
            {title}
          </ExternalLink>
        ) : (
          title
        ))}
      {type === 'progress' && <Progress progress={Number(progress)} />}

      {type?.toLowerCase() === 'address' &&
        (typeof title === 'string' && title?.replace?.(/,/g, '')?.trim() !== '' ? (
          <Typography
            onMouseEnter={() => handleMouseEnter('title')}
            onMouseLeave={handleMouseLeave}
            ref={titleValue}
            sx={titleSx}
            variant="body2"
            fontWeight={500}
            lineHeight={1.4}
            minWidth={100}
            fontSize={16}
            color="#3E3E3E"
            noWrap
          >
            {title || '--'}
          </Typography>
        ) : (
          '--'
        ))}

      {showLabelPopper && (
        <Popper open={showLabelPopper && anchorEl !== null} anchorEl={anchorEl} style={{ width: 'auto', left: '20px' }}>
          <Paper elevation={1}>
            <Typography variant="body2" style={{ padding: 10, fontSize: 12 }}>
              {label}
            </Typography>
          </Paper>
        </Popper>
      )}

      {showTitlePopper && (
        <Popper open={showTitlePopper && anchorEl !== null} anchorEl={anchorEl} style={{ width: 'auto', left: '20px' }}>
          <Paper elevation={1}>
            <Typography variant="body2" style={{ padding: 10, fontSize: 12 }}>
              {title}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
};

export default Label;
